import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Input from "../../components/form/input/InputField";
import CustomMultiSelect from "../../components/common/CustomMultiSelect";
import FileUpload from "../../components/form/FileUpload";
import { styles } from "../../styles/pages/User/UserEditStyles";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import Loader from "../../components/common/Loader";
import ListboxComponent from "../../components/common/ListboxComponent";

type Option = {
  label: string;
  value: string;
};

interface JobTitle {
  id: number;
  job_title: string;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  selectedJobTitle: string; // Stores ID as string
  job_type: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile_number?: string;
  roles?: string;
  jobtitle?: string;
  job_type?: string;
  profile_image?: string;
  generic?: string;
}

const UserEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    selectedJobTitle: "",
    job_type: "",
  });
  const [selectedRoles, setSelectedRoles] = useState<Option[]>([]);
  const [profileImage, setProfileImage] = useState<File | string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(true);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);

  const roleOptions: Option[] = [
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
  ];

  // const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

  // Fetch user data and job titles on mount
  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token || !id) {
        setSuccessModalOpen(false);
        navigate("/signin");
        return;
      }

      try {
        setLoading(true);

        // Fetch job titles first to ensure data is available
        const jobTitlesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/jobtitle/name/list`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (jobTitlesResponse.data.status === 200) {
          setJobTitles(jobTitlesResponse.data.data);
        } else {
          throw new Error("Failed to fetch job titles");
        }

        // Fetch user data
        const userResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/user/edit`,
          { user_id: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (userResponse.data.status !== 200) {
          throw new Error(userResponse.data.message || "Failed to fetch user data");
        }

        const data = userResponse.data.data[0];
        if (!data) {
          throw new Error("No user data found for the given ID");
        }

        const rawJobType = data.job_type?.toString().toLowerCase();

        let mappedJobType: string;
        if (rawJobType === "1" || rawJobType === "admin") {
          mappedJobType = "1";
        } else if (rawJobType === "2" || rawJobType === "regular user") {
          mappedJobType = "2";
        } else {
          mappedJobType = "1"; // default to Admin
        }

        const jobTitleId = data.job_title ? String(data.job_title) : ""; // Use jobtitle as ID

        setFormData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          email: data.email || "",
          mobile_number: data.mobile_number || "",
          selectedJobTitle: jobTitleId,
          job_type: mappedJobType,
        });

        // Set roles
        const formattedRoles = (data.role || []).map((role: string) => ({
          value: role.toLowerCase(),
          label: role,
        }));
        setSelectedRoles(formattedRoles);

        // Set profile image
        setProfileImage(
          data.profile_image && !data.profile_image.includes("null.png")
            ? data.profile_image
            : null
        );
      } catch (error) {
        setErrors({ generic: "Error fetching data. Please try again." });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  // Input change handler
  const handleInputChange =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          [field]: `The ${field.replace("_", " ")} field is required`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  // Mobile number handler
  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, mobile_number: formattedValue }));
    if (!formattedValue) {
      setErrors((prev) => ({
        ...prev,
        mobile_number: "The Mobile number field is required",
      }));
    } else {
      setErrors((prev) => ({ ...prev, mobile_number: undefined }));
    }
  };

  // Roles change handler
  const handleRolesChange = (selected: string[]) => {
    const formattedRoles = selected.map((role) => ({
      value: role.toLowerCase(),
      label: role,
    }));
    setSelectedRoles(formattedRoles);
    setErrors((prev) => ({
      ...prev,
      roles: selected.length === 0 ? "At least one role must be selected" : undefined,
    }));
  };

  // Job title change handler
  const handleJobTitleChange = (job: JobTitle | null) => {
    setFormData((prev) => ({ ...prev, selectedJobTitle: job ? String(job.id) : "" }));
    setErrors((prev) => ({
      ...prev,
      jobtitle: !job ? "The job title field is required" : undefined,
    }));
  };

  // Job type change handler
  const handleJobTypeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, job_type: value }));
    setErrors((prev) => ({ ...prev, job_type: undefined }));
  };

  // Profile image handler
  const handleProfileImage = (file: File | null) => {
    setProfileImage(file);
    setErrors((prev) => ({ ...prev, profile_image: undefined }));
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const { first_name, last_name, email, mobile_number, selectedJobTitle, job_type } = formData;
    const newErrors: FormErrors = {};

    if (!first_name) newErrors.first_name = "The First name field is required";
    if (!last_name) newErrors.last_name = "The Last name field is required";
    if (!email) newErrors.email = "The Email field is required";
    if (!mobile_number) newErrors.mobile_number = "The Mobile number field is required";
    if (selectedRoles.length === 0) newErrors.roles = "At least one role must be selected";
    if (!selectedJobTitle) newErrors.jobtitle = "The job title field is required";
    if (!job_type) newErrors.job_type = "The job type field is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");
    if (!token || !id) {
      setErrors({ email: "Authorization required. Please log in." });
      navigate("/signin");
      setSaving(false);
      return;
    }

    const submitData = new FormData();
    submitData.append("user_id", id);
    submitData.append("first_name", first_name);
    submitData.append("last_name", last_name);
    submitData.append("email", email);
    submitData.append("mobile_number", mobile_number);
    selectedRoles.forEach((role) => submitData.append("role[]", role.value));
    submitData.append("job_title", selectedJobTitle);
    submitData.append("job_type", job_type);
    if (profileImage instanceof File) {
      submitData.append("profile_image", profileImage);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/update`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/user-list");
        }, 2000);
      } else if (response.data.status === 422) {
        const apiErrors = response.data.message || response.data.errors;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            )
          );
        }
      } else if (response.data.status === 500) {
        setErrors({ email: response.data.message || "Internal Server Error" });
      } else {
        setErrors({ email: "Unexpected server response. Please try again." });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ email: errorMessage });

      if (error.response?.status === 422) {
        const apiErrors = error.response?.data?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            )
          );
        }
      } else if (error.response?.status === 401) {
        navigate("/signin");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Edit User</h2>
      </div>
      {loading && (
        <div className="mt-5">
          <Loader />
        </div>
      )}
      {!loading && (
        <div className={styles.formContainer}>
          <form
            ref={formRef}
            className={styles.form}
            onSubmit={handleSubmit}
            noValidate
          >
            <div className={styles.profileImageSection}>
              <label className={styles.label}>Profile Image</label>
              <div>
                <FileUpload
                  onFileSelect={handleProfileImage}
                  profileImageUrl={
                    typeof profileImage === "string" ? profileImage : undefined
                  }
                  showDelete={true}
                  userId={id}
                />
              </div>
            </div>

            <div className={styles.userDetails}>
              <div>
                <label className={styles.label}>
                  First Name <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.first_name}
                  onChange={handleInputChange("first_name")}
                  className={`${styles.inputField} ${errors.first_name ? styles.inputError : styles.inputDefault
                    }`}
                />
                {errors.first_name && (
                  <span className={styles.errorText}>{errors.first_name}</span>
                )}
              </div>

              <div>
                <label className={styles.label}>
                  Last Name <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.last_name}
                  onChange={handleInputChange("last_name")}
                  className={`${styles.inputField} ${errors.last_name ? styles.inputError : styles.inputDefault
                    }`}
                />
                {errors.last_name && (
                  <span className={styles.errorText}>{errors.last_name}</span>
                )}
              </div>

              <div>
                <label className={styles.label}>
                  Email <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  className={`${styles.inputField} ${errors.email ? styles.inputError : styles.inputDefault
                    }`}
                />
                {errors.email && (
                  <span className={styles.errorText}>{errors.email}</span>
                )}
              </div>

              <div>
                <label className={styles.label}>
                  Mobile Number <span className={styles.required}>*</span>
                </label>
                <Input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={handleMobileNumberChange}
                  className={`${styles.inputField} ${errors.mobile_number ? styles.inputError : styles.inputDefault
                    }`}
                />
                {errors.mobile_number && (
                  <span className={styles.errorText}>{errors.mobile_number}</span>
                )}
              </div>

              <div className={styles.rolesSection}>
                <label className={styles.label}>
                  Select a Roles <span className={styles.required}>*</span>
                </label>
                <div className={styles.rolesContainer}>
                  <CustomMultiSelect
                    options={roleOptions}
                    value={selectedRoles.map((role) => role.value)}
                    onChange={handleRolesChange}
                    error={!!errors.roles}
                  />
                </div>
                {errors.roles && (
                  <span className={styles.errorText}>{errors.roles}</span>
                )}
              </div>

              <div className={styles.rolesSection}>
                <div className={styles.rolesContainer}>
                  <ListboxComponent
                    value={
                      jobTitles.find((job) => job.id === Number(formData.selectedJobTitle)) ?? null
                    }
                    onChange={handleJobTitleChange}
                    items={jobTitles}
                    label="Job Title"
                    displayKey="job_title"
                    error={errors.jobtitle}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={styles.label}>
                  Job Type <span className={styles.required}>*</span>
                </label>
                <div className={styles.radioGroup}>
                  {[
                    { value: "1", label: "Admin" },
                    { value: "2", label: "Regular User" },
                  ].map((type) => (
                    <label key={type.value} className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="job_type"
                        value={type.value}
                        checked={formData.job_type === type.value}
                        onChange={() => handleJobTypeChange(type.value)}
                        className={styles.radioInput}
                      />
                      {type.label}
                    </label>
                  ))}
                </div>
                {errors.job_type && (
                  <span className={styles.errorText}>{errors.job_type}</span>
                )}
              </div>
            </div>

            <div className={styles.buttonsContainer}>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className={styles.cancelButton}
                type="button"
                onClick={() => navigate("/user-list")}
              >
                Cancel
              </button>
            </div>
          </form>

          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className={styles.modal}
          >
            <button
              onClick={() => setSuccessModalOpen(false)}
              className={styles.modalCloseButton}
            ></button>
            <div className={styles.modalIconContainer}>
              <CheckCircle className="w-6 h-6" />
            </div>
            <h4 className={styles.modalTitle}>User Updated Successfully</h4>
            <p className={styles.modalMessage}>
              The user has been updated in the system successfully.
            </p>
            <div className={styles.modalButtonContainer}>
              <button
                onClick={() => setSuccessModalOpen(false)}
                className={styles.modalButton}
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default UserEdit;